/**
 * AufgabePeek — the Aufgaben decision side-panel (T5 "Details öffnen seitlich").
 *
 * STEP 3 scope: the full decision surface. It loads the EXISTING GET /tasks/:id
 * split-view (useTaskSplitView, live mode) and renders the Fakten — task facts +
 * the linked communication + attachments + activity/Historie — then a DecisionBar
 * footer (Erledigen = green primary · In Arbeit · Abbrechen offset; terminal tasks
 * offer Wieder öffnen). The actions are wired by the host screen (useTaskDecision +
 * Auto-Next). In mock mode the split-view query is disabled and the communication
 * facts fall back to the loaded TaskSummary row (subject/from/received_at).
 *
 * The panel renders the SERVER's facts + status — it computes no transition
 * client-side (coding-standards rule #2). Keyboard J/K/E/Enter/Esc are owned by
 * the host screen (mirrors EingangScreen).
 *
 * [Source: apps/web/src/components/data/DecisionPeek.tsx (panel primitive)]
 * [Source: apps/web/src/components/data/DecisionBar.tsx (decision pattern K1)]
 * [Source: apps/web/src/features/communication-v2/EingangScreen.tsx (task-backed split)]
 */
import type { ReactNode } from 'react';
import { CheckCircle2, Clock, Paperclip, RotateCcw, XCircle } from 'lucide-react';
import { DecisionPeek } from '../../../components/data/DecisionPeek';
import { DecisionBar } from '../../../components/data/DecisionBar';
import { StatusDot } from '../../../components/ui/StatusDot';
import { Badge } from '../../../components/ui/Badge';
import { dateTimeShortDe, fileSize } from '../../../lib/format';
import { useTaskSplitView } from '../hooks/useTaskSplitView';
import {
  processLabel,
  statusLabel,
  STATUS_TONE,
  derivePriority,
  PRIORITY_LABEL,
  PRIORITY_VARIANT,
  faellig,
  sourceLabel,
  assigneeLabel,
} from '../api/aufgaben-api';
import type { TaskRow } from '../types';
import { REGISTER_BEFUND_TASK_TITLE, RETENTION_TASK, TERMINATION_TASK, REGISTRATION_ALTBESTAND_TASK, REGISTRATION_INHERITANCE_TASK, REGISTRATION_REMINDER_TASK_KINDS } from '@brokly/shared';
import { RegisterBefundActions } from '../../mandate/RegisterBefundActions';
import { RegistrationProposalReview } from '../../mandate/RegistrationProposalReview';
import { RegistrationZuordnen } from '../../mandate/RegistrationZuordnen';
import { RegistrationTaskSection } from '../../mandate/RegistrationTaskSection';
import { AltbestandTaskSection } from '../../mandate/AltbestandTaskSection';
import { VererbungTaskSection } from '../../mandate/VererbungTaskSection';
import { isNotFound, useRegistrationProposal, useRegistrationZuordnen } from '../../mandate/mandate-registration-queries';
import { registrationErrorText } from '../../mandate/registration-error-text';
import { PruefberichtContainer } from '../../entity-detail/components/PruefberichtContainer';
import { UebergabeTaskSection } from '../../mandate/termination/UebergabeTaskSection';
import { LoeschvorschlagSection } from './LoeschvorschlagSection';
import {
  TerminationFreigabeSection,
  TerminationTaskSection,
  isTerminationFreigabeTask,
  terminationSectionTitle,
  terminationTaskKind,
} from '../../mandate/termination/TerminationTaskSection';

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-b-0">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-right text-sm text-foreground">{children}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-4">
      <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h4>
      {children}
    </div>
  );
}

/** A read of a registration section that failed for another reason than «not this kind of task». */
function SectionError({ error }: { error: unknown }) {
  return (
    <p className="text-sm text-destructive" role="alert">
      {registrationErrorText(error)}
    </p>
  );
}

/** The registration kinds whose task carries a parsed insurer reply (the review section). */
const PROPOSAL_KINDS: ReadonlySet<string> = new Set(['eintragungsbestaetigung_pruefen', 'widerspruechliche_antwort']);
/** The registration kind of a mail no registration could be matched to (the Zuordnen section). */
const ZUORDNEN_KIND = 'eintragungsbestaetigung_zuordnen';

/** The review «Eintragungsbestätigung» of a task with an insurer reply; a 404 shows nothing. */
function ProposalSection({ taskId }: { taskId: string }) {
  const proposal = useRegistrationProposal(taskId);
  if (proposal.data !== undefined) {
    return (
      <Section title="Eintragungsbestätigung">
        <RegistrationProposalReview taskId={taskId} />
      </Section>
    );
  }
  if (proposal.isError && !isNotFound(proposal.error)) {
    return (
      <Section title="Eintragungsbestätigung">
        <SectionError error={proposal.error} />
      </Section>
    );
  }
  return null;
}

/** The candidate registrations of a Zuordnen task; a 404 shows nothing. */
function ZuordnenSection({ taskId }: { taskId: string }) {
  const zuordnen = useRegistrationZuordnen(taskId);
  if (zuordnen.data !== undefined) {
    return (
      <Section title="Zuordnen">
        <RegistrationZuordnen taskId={taskId} data={zuordnen.data} />
      </Section>
    );
  }
  if (zuordnen.isError && !isNotFound(zuordnen.error)) {
    return (
      <Section title="Zuordnen">
        <SectionError error={zuordnen.error} />
      </Section>
    );
  }
  return null;
}

/**
 * The Brokermandat registration sections of a mandate_registration task, mounted by the task's
 * registration_kind (p5s5-r2-L1): the review «Eintragungsbestätigung» for eintragungsbestaetigung_pruefen
 * and widerspruechliche_antwort, «Zuordnen» for eintragungsbestaetigung_zuordnen, the registration with
 * S5's decisions for a task of the reminder loop (an escalation, a failed reminder), nothing (and no
 * read) for any other kind. A row without the field (undefined: a source that
 * does not carry it, the mock data) falls back to the data: both reads, a 404 shows nothing. A section
 * that once answered stays (a refresh after «Zuordnen» may 404 the read; TanStack keeps the last data,
 * so the confirmation stays visible).
 */
function MandateRegistrationSections({
  taskId,
  kind,
  subjectType,
  subjectId,
}: {
  taskId: string;
  kind: string | null | undefined;
  subjectType: string | null | undefined;
  subjectId: string | null | undefined;
}) {
  if (kind === undefined) {
    return (
      <>
        <ProposalSection taskId={taskId} />
        <ZuordnenSection taskId={taskId} />
      </>
    );
  }
  if (kind !== null && PROPOSAL_KINDS.has(kind)) return <ProposalSection taskId={taskId} />;
  if (kind === ZUORDNEN_KIND) return <ZuordnenSection taskId={taskId} />;
  if (kind === REGISTRATION_ALTBESTAND_TASK.kind && (subjectType === 'person' || subjectType === 'firma') && subjectId) {
    return (
      <Section title="Altbestand">
        <AltbestandTaskSection taskId={taskId} subjectType={subjectType} subjectId={subjectId} />
      </Section>
    );
  }
  if (kind === REGISTRATION_INHERITANCE_TASK.VERERBUNG_PRUEFEN.kind) {
    return (
      <Section title="Vererbung">
        <VererbungTaskSection taskId={taskId} />
      </Section>
    );
  }
  if (kind !== null && REGISTRATION_REMINDER_TASK_KINDS.includes(kind) && (subjectType === 'person' || subjectType === 'firma') && subjectId) {
    return (
      <Section title="Meldung an Versicherer">
        <RegistrationTaskSection taskId={taskId} subjectType={subjectType} subjectId={subjectId} />
      </Section>
    );
  }
  return null;
}

interface AufgabePeekProps {
  task: TaskRow;
  index: number;
  total: number;
  busy?: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onComplete: () => void;
  onInProgress: () => void;
  onCancel: () => void;
  onReopen: () => void;
}

export function AufgabePeek({
  task,
  index,
  total,
  busy = false,
  onClose,
  onPrev,
  onNext,
  onComplete,
  onInProgress,
  onCancel,
  onReopen,
}: AufgabePeekProps) {
  const prio = derivePriority(task);
  const f = faellig(task.due_at);
  const isTerminal = task.status === 'done' || task.status === 'cancelled';
  // A task the intake opened for a customer's Kündigung (told by its title).
  const terminationKind = task.process === 'mandate_termination' ? terminationTaskKind(task.title) : null;
  // The Freigabe task of an eröffnete termination (the header chip links here): «Mandat auflösen».
  const terminationFreigabe = task.process === 'mandate_termination' && isTerminationFreigabeTask(task.title);

  // Live split-view (task + comm + attachments + activity); disabled in mock mode.
  const { data: split } = useTaskSplitView(task.id);
  const attachments = split?.attachments ?? [];
  const activity = split?.activity ?? [];

  // Communication facts: prefer the split-view comm; fall back to the row summary
  // (TaskSummary carries subject/from/received_at joined from the communication).
  const commSubject = split?.communication?.subject ?? task.subject ?? null;
  const commFrom = split?.communication?.from ?? task.from ?? null;
  const commReceived = split?.communication?.received_at ?? task.received_at ?? null;
  const commBody = split?.communication?.body ?? null;
  const hasComm = !!(commSubject || commFrom || commBody);

  // Decision footer — a terminal task offers "Wieder öffnen"; an active task the
  // Erledigen / In Arbeit / Aufgabe abbrechen ladder (reject action deliberately offset).
  // The reject action CANCELS the task (POST /tasks/:id/cancel), so it is labelled
  // "Aufgabe abbrechen" — a bare "Abbrechen" reads as "close this panel", which it is not.
  // P6-S7 (review H6): the Löschvorschlag closes through its own section, never through the generic ladder,
  // and a closed one is not reopened (the api refuses it: no route could close it again).
  const isRetentionProposal = task.process === 'customer_retention' && task.registration_kind === RETENTION_TASK.LOESCHVORSCHLAG.kind;
  const footer = isRetentionProposal ? undefined : isTerminal ? (
    <DecisionBar
      primary={{ label: 'Wieder öffnen', icon: <RotateCcw className="size-4" />, onClick: busy ? undefined : onReopen }}
    />
  ) : (
    <DecisionBar
      primary={{ label: 'Erledigen', icon: <CheckCircle2 className="size-4" />, onClick: busy ? undefined : onComplete }}
      alternatives={[
        { label: 'In Arbeit', icon: <Clock className="size-4" />, onClick: busy ? undefined : onInProgress },
      ]}
      reject={{ label: 'Aufgabe abbrechen', icon: <XCircle className="size-4" />, onClick: busy ? undefined : onCancel }}
    />
  );

  return (
    <DecisionPeek
      title={task.title ?? '(ohne Titel)'}
      subtitle={processLabel(task.process)}
      progress={`${index + 1} / ${total}`}
      onClose={onClose}
      onPrev={onPrev}
      onNext={onNext}
      footer={footer}
    >
      {/* ── Fakten (task) ── */}
      <div className="flex flex-col">
        <Fact label="Status">
          <StatusDot tone={STATUS_TONE[task.status]} label={statusLabel(task.status)} />
        </Fact>
        <Fact label="Priorität">
          <Badge variant={PRIORITY_VARIANT[prio]}>{PRIORITY_LABEL[prio]}</Badge>
        </Fact>
        <Fact label="Fälligkeit">
          <StatusDot tone={f.tone} label={f.text} />
        </Fact>
        {task.kunde_name && <Fact label="Kunde">{task.kunde_name}</Fact>}
        <Fact label="Zuständig">{assigneeLabel(task)}</Fact>
        <Fact label="Quelle">{sourceLabel(task)}</Fact>
        {task.confidence !== null && (
          <Fact label="Konfidenz">{(Number(task.confidence) * 100).toFixed(0)} %</Fact>
        )}
        {task.needs_review && (
          <Fact label="Hinweis">
            <span className="text-status-warning">Prüfung erforderlich</span>
          </Fact>
        )}
        <Fact label="Erstellt">{dateTimeShortDe(task.created_at)}</Fact>
      </div>

      {/* ── Brokermandat: the exits of a Register-Befund lock (Freigeben / Vorgang abbrechen) ── */}
      {task.process === 'mandate_signing' &&
        task.title === REGISTER_BEFUND_TASK_TITLE &&
        !isTerminal &&
        (task.subject_type === 'person' || task.subject_type === 'firma') &&
        task.subject_id !== null && (
          <Section title="Register-Befund">
            <RegisterBefundActions subjectType={task.subject_type} subjectId={task.subject_id} />
          </Section>
        )}

      {/* ── The Prüfbericht of a mandate review task (decided right here) ── */}
      {task.process === 'mandate_signing' && task.pruefbericht_vorgang_id ? (
        <Section title="Prüfbericht">
          <PruefberichtContainer vorgangId={task.pruefbericht_vorgang_id} />
        </Section>
      ) : null}

      {/* ── The insurer's reply to a Brokermandat registration (review / Zuordnen, by the task's kind) ── */}
      {task.process === 'mandate_registration' && (
        <MandateRegistrationSections
          key={task.id}
          taskId={task.id}
          kind={task.registration_kind}
          subjectType={task.subject_type}
          subjectId={task.subject_id}
        />
      )}

      {/* ── The Übergabe of a terminated mandate: the live checklist (closed with «Erledigen») ── */}
      {task.process === 'mandate_termination' &&
        (task.registration_kind === TERMINATION_TASK.UEBERGABE.kind || task.title === TERMINATION_TASK.UEBERGABE.title) && (
          <Section title="Übergabe offener Vorgänge">
            <UebergabeTaskSection key={task.id} taskId={task.id} />
          </Section>
        )}
      {/* ── A customer's Kündigung read by the intake (proposal, Berechtigungsprüfung, hints) ── */}
      {terminationKind !== null && (
        <Section title={terminationSectionTitle(terminationKind)}>
          <TerminationTaskSection
            key={task.id}
            taskId={task.id}
            kind={terminationKind}
            subjectType={task.subject_type}
            subjectId={task.subject_id}
            active={!isTerminal}
          />
        </Section>
      )}
      {terminationFreigabe && (
        <Section title="Freigabe">
          <TerminationFreigabeSection key={task.id} subjectType={task.subject_type} subjectId={task.subject_id} active={!isTerminal} />
        </Section>
      )}

      {/* ── The Löschvorschlag of a customer whose retention ran out (P6-S7) ── */}
      {isRetentionProposal &&
        !isTerminal &&
        task.retention_proposal &&
        (task.subject_type === 'person' || task.subject_type === 'firma') &&
        task.subject_id !== null && (
          <Section title="Löschvorschlag">
            <LoeschvorschlagSection
              key={task.id}
              subjectType={task.subject_type}
              subjectId={task.subject_id}
              confirmationLabel={task.retention_proposal.confirmation_label}
              retentionUntil={task.retention_proposal.retention_until}
            />
          </Section>
        )}

      {/* ── Kommunikation (left pane of the split-view) ── */}
      {hasComm && (
        <Section title="Kommunikation">
          <div className="rounded-md border border-border bg-background p-3">
            {commSubject && <p className="text-sm font-medium text-foreground">{commSubject}</p>}
            {(commFrom || commReceived) && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {commFrom}
                {commFrom && commReceived ? ' · ' : ''}
                {commReceived ? dateTimeShortDe(commReceived) : ''}
              </p>
            )}
            {commBody && (
              <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground line-clamp-6">
                {commBody}
              </p>
            )}
          </div>
        </Section>
      )}

      {/* ── Anhänge ── */}
      {attachments.length > 0 && (
        <Section title="Anhänge">
          <ul className="flex flex-col gap-1">
            {attachments.map((a) => (
              <li
                key={a.id}
                className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground"
              >
                <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{a.filename}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{fileSize(a.size_bytes)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* ── Historie (activity timeline — append-only Doku) ── */}
      {activity.length > 0 && (
        <Section title="Historie">
          <ol className="flex flex-col gap-2 border-l border-border pl-3">
            {activity.map((entry) => (
              <li key={entry.id} className="relative">
                <span className="absolute -left-[1.1rem] top-1.5 size-1.5 rounded-full bg-border" />
                <p className="text-sm text-foreground">{entry.content ?? entry.type}</p>
                <p className="text-xs text-muted-foreground">{dateTimeShortDe(entry.created_at)}</p>
              </li>
            ))}
          </ol>
        </Section>
      )}
    </DecisionPeek>
  );
}
